import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, Download, FileText } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ShareRegisterCardProps {
  companyId: string;
}

export default function ShareRegisterCard({ companyId }: ShareRegisterCardProps) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [exporting, setExporting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      let query = supabase.from('share_register').select('*').eq('company_id', companyId);

      if (filter !== 'ALL') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query.order('issue_date', { ascending: false });

      if (error) {
        toast({
          title: 'Error loading share register',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        setRows(data || []);
      }
    } catch (error) {
      console.error('Error loading share register:', error);
      toast({
        title: 'Error',
        description: 'Failed to load share register',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function exportRegister() {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-share-register', {
        body: { companyId, format: 'csv' },
      });

      if (error) {
        toast({
          title: 'Export failed',
          description: error.message,
          variant: 'destructive',
        });
      } else if (data) {
        // Create a blob from the CSV data and download it
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `share-register-${companyId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast({
          title: 'Export successful',
          description: 'Share register exported to CSV',
        });
      }
    } catch (error) {
      console.error('Error exporting share register:', error);
      toast({
        title: 'Error',
        description: 'Failed to export share register',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load();

    // The register is a view over share_certificates, which a trigger rewrites
    // on every ledger event. Without this the card showed the holding as it was
    // before the movement until someone pressed Refresh — the cap table above it
    // updated, and the register below it did not.
    //
    // Subscribed to ledger_events rather than share_certificates because that is
    // the table in the supabase_realtime publication; share_certificates is not,
    // so a subscription to it would report nothing and look like it worked. The
    // trigger runs inside the same transaction as the ledger insert, so by the
    // time this fires the certificates are already written.
    const channel = supabase
      .channel(`share-register-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ledger_events',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          load();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, filter]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Share Register
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="REPLACED">Replaced</SelectItem>
              <SelectItem value="CONVERTED">Converted</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={exportRegister} disabled={exporting || rows.length === 0}>
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            No share register entries found. Shares will appear here when issued.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certificate #</TableHead>
                <TableHead>Holder</TableHead>
                <TableHead>Class</TableHead>
                <TableHead className="text-right">Shares</TableHead>
                <TableHead className="text-right">Price/Share</TableHead>
                <TableHead>Instrument</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Issue Date</TableHead>
                <TableHead>Transfer Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-sm">{r.certificate_number || '—'}</TableCell>
                  <TableCell className="font-medium">{r.holder_name}</TableCell>
                  <TableCell>{r.share_class}</TableCell>
                  <TableCell className="text-right">{Number(r.shares).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {r.price_per_share ? Number(r.price_per_share).toFixed(4) : '—'}
                  </TableCell>
                  <TableCell>{r.instrument}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        r.status === 'ISSUED'
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : r.status === 'TRANSFERRED'
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                            : 'bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}
                    >
                      {r.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {r.issue_date ? new Date(r.issue_date).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    {r.transfer_date ? new Date(r.transfer_date).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
